/**
 * Shared edge deserialization for every path that rebuilds a graph from a
 * serialized schema — loading a saved strategy AND inserting/pasting a schema
 * JSON. Both used to filter edges independently, and they had diverged:
 *
 *  - The load path kept any edge whose target node resolves and that carries a
 *    target handle (permissive, correct).
 *  - The insert-schema path used a hardcoded per-node-type handle ALLOWLIST
 *    whose default case only accepted `input_N`/empty handles. That silently
 *    dropped valid connections to every node whose real input handle wasn't
 *    explicitly listed: positionSizing / stop / trailingStop / timeout
 *    ('signal'), the trade node's 'trailing' input, and every filter node
 *    ('data'). Pasting a copied schema lost those connections.
 *
 * Connection validity is already enforced where connections are *created*
 * (validateNodeConnection on manual connect). Deserialization must therefore
 * only enforce STRUCTURAL validity — the endpoints resolve and a handle exists.
 * Inventing a second, stricter, drift-prone allowlist here is what caused the
 * bug. This module is the single source of truth both paths now call.
 *
 * In addition to the structural check above we now consult the schema registry
 * to drop edges whose target/source handle id is provably absent from the
 * node's CURRENT schema. This catches stale edges that survived schema drift —
 * e.g. a node whose `data.type` no longer resolves to a registered schema (the
 * component falls back to "Unknown Node Type" UI with no handles, and React
 * Flow logs a "Couldn't create edge for handle id" warning on every paint).
 * The check is registry-DERIVED, not hardcoded, so it can't reintroduce the
 * historical drift bug: if a schema declares an input, this allowlist contains
 * it. Multi-input handlers expand to input_1..input_${maxInputs} since
 * getDynamicInputs synthesises those on demand.
 */

import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import { isDeadSocket } from '@/features/backtest/schemas/input-activity'
import { isPassthroughType } from '@/features/backtest/utils/inline-passthroughs'

type RawNode = Record<string, unknown>
type RawEdge = Record<string, unknown>

/**
 * Technical-indicator node types whose legacy single `data` input handle was
 * renamed to `input_1`. Old saved/exported schemas still carry `data`; we
 * migrate it on the way in so the edge lands on the current handle. Mirrors the
 * list the load path historically used.
 */
const LEGACY_DATA_INDICATOR_TYPES = new Set<string>([
  'simplemovingaverage',
  'exponentialmovingaverage',
  'weightedmovingaverage',
  'relativestrengthindex',
  'macd',
  'bollinger-bands',
  'atr',
  'adx',
  'stochastic-oscillator',
  'parabolic-sar',
  'donchian-channel',
  'keltner-channel',
  'aroon',
  'kst',
  'mfi',
  'obv',
  'roc',
  'awesome-oscillator',
  'adi',
  'vwap',
  'stochastic-rsi',
  'williams-r',
  'trix',
])

/** Effective node type: prefer `data.type`, fall back to the node's `type`. */
function nodeTypeOf(node: RawNode | undefined): string {
  if (!node) return ''
  const dataType = (node.data as Record<string, unknown> | undefined)?.type
  return (dataType || node.type || '') as string
}

/**
 * Rewrite a legacy `data` target handle to `input_1` when the target is a
 * technical indicator. No-op for every other edge.
 */
export function migrateLegacyDataHandle(edge: RawEdge, nodesById: Map<string, RawNode>): RawEdge {
  if (edge.targetHandle !== 'data') return edge
  const target = nodesById.get(edge.target as string)
  if (!target) return edge
  if (LEGACY_DATA_INDICATOR_TYPES.has(nodeTypeOf(target))) {
    return { ...edge, targetHandle: 'input_1' }
  }
  return edge
}

/**
 * Look up the registered schema for a node by its effective type + preset id.
 * Uses the alias-aware lookup so renamed node types (registered via
 * `UniversalNodeRegistry.registerAlias`) resolve to their new schema — without
 * this, a legacy `entry`-typed node whose schema moved to `entryRules` would
 * fail registry lookup, and the sweep would drop the (still-valid) edge.
 */
function resolveSchema(node: RawNode | undefined) {
  if (!node) return undefined
  const type = nodeTypeOf(node)
  const presetId = (node.data as Record<string, unknown> | undefined)?.presetId as
    | string
    | undefined
  const schemaKey = presetId ? `${type}:${presetId}` : type
  return UniversalNodeRegistry.getSchemaResolved(schemaKey)
}

/**
 * Maximum set of input handle ids the node's universal-node component can
 * render. Empty when the node has no resolvable schema — the component shows
 * the "Unknown Node Type" fallback with no handles, so every edge to it is
 * stale. For multi-input handlers we include input_1..input_${maxInputs}
 * because getDynamicInputs synthesises those on demand.
 */
function maxValidInputHandleIds(node: RawNode | undefined): Set<string> {
  const schema = resolveSchema(node)
  if (!schema) return new Set()
  const ids = new Set<string>(schema.inputs.map((i) => i.id))
  if (schema.inputHandler?.type === 'multi') {
    const max = schema.inputHandler.maxInputs ?? 50
    for (let i = 1; i <= max; i++) ids.add(`input_${i}`)
  }
  return ids
}

/** Maximum set of output handle ids; empty when schema isn't registered. */
function maxValidOutputHandleIds(node: RawNode | undefined): Set<string> {
  const schema = resolveSchema(node)
  if (!schema) return new Set()
  return new Set(schema.outputs.map((o) => o.id))
}

/**
 * An edge survives deserialization iff:
 *  - its target node resolves, it carries a target handle, and the handle id
 *    is one the target node's current schema could render; AND
 *  - if a sourceHandle is specified, the source node's schema lists it as an
 *    output (sourceHandle is optional — single-output nodes omit it).
 */
export function isDeserializableEdge(edge: RawEdge, nodesById: Map<string, RawNode>): boolean {
  const targetNode = nodesById.get(edge.target as string)
  if (!targetNode) return false
  if (!edge.targetHandle) return false

  // Group I/O pass-throughs have no registry schema — their single target
  // handle is always 'in'. Without this, every edge INTO a pill (external
  // source → groupInput, internal source → groupOutput) was dropped on load.
  if (isPassthroughType(nodeTypeOf(targetNode))) {
    return edge.targetHandle === 'in'
  }

  const validTargets = maxValidInputHandleIds(targetNode)
  if (validTargets.size === 0) return false
  if (!validTargets.has(edge.targetHandle as string)) return false

  if (edge.sourceHandle) {
    const sourceNode = nodesById.get(edge.source as string)
    if (sourceNode) {
      const validSources = maxValidOutputHandleIds(sourceNode)
      if (validSources.size > 0 && !validSources.has(edge.sourceHandle as string)) {
        return false
      }
    }
  }
  return true
}

/** Index nodes by id for O(1) edge resolution. */
export function indexNodesById(nodes: RawNode[]): Map<string, RawNode> {
  return new Map(nodes.map((n) => [n.id as string, n]))
}

/**
 * Migrate legacy handles then drop structurally-invalid edges. The single
 * deserialization chokepoint shared by strategy load and schema insert.
 */
export function deserializeEdges(rawEdges: RawEdge[], nodes: RawNode[]): RawEdge[] {
  const nodesById = indexNodesById(nodes)
  return rawEdges
    .map((edge) => withoutDrawnEdgeData(migrateLegacyDataHandle(edge, nodesById)))
    .filter((edge) => isDeserializableEdge(edge, nodesById))
    .filter((edge) => !isDeadWire(edge, nodesById))
}

/**
 * A wire into a socket its node's mode does not read (a Multi Target Trade
 * Manager's price level): the run never reads it, and the face would draw it
 * beside the mode's own socket, so load lets it go (input-activity
 * isDeadSocket).
 */
function isDeadWire(edge: RawEdge, nodesById: Map<string, RawNode>): boolean {
  const target = nodesById.get(edge.target as string)
  const schema = target && UniversalNodeRegistry.getSchema(target.type as string)
  if (!schema || typeof edge.targetHandle !== 'string') return false
  const data = target.data as { parameters?: unknown } | undefined
  return isDeadSocket(schema, edge.targetHandle, data?.parameters)
}

/**
 * What the canvas draws on a wire, computed at render time and never stored
 * (connection audit step 8, 2026-10-06): off (`disabled`, from the input
 * switch it feeds), dead, highlighted, and the drag ref. A strategy saved
 * before then may carry them; a stale `disabled` pruned a live branch from
 * the run. Load and save both drop them.
 */
const DRAWN_EDGE_DATA = [
  'disabled',
  'deadOutput',
  'highlighted',
  'wireHighlight',
  'isDraggingRef',
] as const

/** The edge without the canvas's drawn flags in its data; the same edge when it has none. */
export function withoutDrawnEdgeData<E extends { data?: unknown }>(edge: E): E {
  const data = edge.data as Record<string, unknown> | undefined
  if (!data || !DRAWN_EDGE_DATA.some((key) => key in data)) return edge
  const kept = { ...data }
  for (const key of DRAWN_EDGE_DATA) delete kept[key]
  return { ...edge, data: kept }
}
